<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { $fetch } from '@/plugins/fetch';
import type {
  AssistantCreateSessionData,
  AssistantCreatedSession,
  AssistantSessionListItem,
  AssistantSessionNavigationOptions,
  AssistantSessionSummary,
  AssistantSessionUpdate,
} from './types';

const props = defineProps<{
  activeConversationId: string;
  sessionUpdate: AssistantSessionUpdate | null;
}>();

const emit = defineEmits<{
  back: [];
  error: [message: string];
  selectSession: [conversationId: string, options?: AssistantSessionNavigationOptions];
  sessionCreated: [session: AssistantCreatedSession, options?: AssistantSessionNavigationOptions];
  sessionsChange: [sessions: AssistantSessionSummary[]];
}>();

const SESSION_STORAGE_KEY = 'openatom_assistant_sessions';
const ACTIVE_SESSION_STORAGE_KEY = 'openatom_assistant_active_session';
const DEFAULT_SESSION_TITLE = '新的咨询';
const EMPTY_SESSION_PREVIEW = '还没有提问';
const CHANNEL_KEY = import.meta.env.VITE_ASSISTANT_CHANNEL_KEY || '';

const sessions = ref<AssistantSessionSummary[]>([]);
const isCreating = ref(false);

const visibleSessions = computed<AssistantSessionListItem[]>(() => [...sessions.value]
  .sort((a, b) => b.updatedAt - a.updatedAt)
  .map(session => ({
    ...session,
    active: session.conversationId === props.activeConversationId,
    updatedAtText: formatUpdatedAt(session.updatedAt),
  })));

const safeParseSessions = (raw: string | null): AssistantSessionSummary[] => {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((item): item is AssistantSessionSummary => Boolean(
        item
        && typeof item.conversationId === 'string'
        && typeof item.title === 'string'
        && typeof item.preview === 'string'
        && typeof item.updatedAt === 'number',
      ));
  }
  catch {
    return [];
  }
};

const saveSessions = () => {
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions.value));

  emit('sessionsChange', sessions.value);
};

const saveActiveSession = (conversationId: string) => {
  window.localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, conversationId);
};

const formatUpdatedAt = (updatedAt: number) => {
  if (!updatedAt) {
    return '刚刚';
  }

  const now = Date.now();
  const diff = now - updatedAt;

  if (diff < 60 * 1000) {
    return '刚刚';
  }

  if (diff < 60 * 60 * 1000) {
    return `${Math.floor(diff / (60 * 1000))} 分钟前`;
  }

  const date = new Date(updatedAt);
  const today = new Date(now);

  if (date.toDateString() === today.toDateString()) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const selectSession = (conversationId: string, options?: AssistantSessionNavigationOptions) => {
  if (!sessions.value.some(session => session.conversationId === conversationId)) {
    return;
  }

  saveActiveSession(conversationId);

  emit('selectSession', conversationId, options);
};

const applySessionUpdate = (sessionUpdate: AssistantSessionUpdate | null) => {
  if (!sessionUpdate) {
    return;
  }

  let changed = false;

  sessions.value = sessions.value.map((session) => {
    if (session.conversationId !== sessionUpdate.conversationId) {
      return session;
    }

    changed = true;

    return {
      ...session,
      title: sessionUpdate.title ?? session.title,
      preview: sessionUpdate.preview ?? session.preview,
      updatedAt: sessionUpdate.updatedAt ?? session.updatedAt,
    };
  });

  if (changed) {
    saveSessions();
  }
};

const createSession = async (options?: AssistantSessionNavigationOptions) => {
  if (isCreating.value) {
    return;
  }

  isCreating.value = true;

  const { isSuccess, data, msg } = await $fetch<AssistantCreateSessionData>('/chat/v1/sessions', {
    headers: {
      Authorization: `Bearer ${CHANNEL_KEY}`,
    },
  });

  if (!isSuccess || !data?.conversationId) {
    emit('error', msg || '创建会话失败，请稍后再试。');

    isCreating.value = false;

    return;
  }

  const nextSession: AssistantSessionSummary = {
    conversationId: data.conversationId,
    title: DEFAULT_SESSION_TITLE,
    preview: EMPTY_SESSION_PREVIEW,
    updatedAt: Date.now(),
  };

  sessions.value = [
    nextSession,
    ...sessions.value.filter(session => session.conversationId !== nextSession.conversationId),
  ];

  saveSessions();

  selectSession(nextSession.conversationId, options);

  emit('sessionCreated', {
    conversationId: data.conversationId,
    greeting: data.greeting,
    recommendedQuestions: data.recommendedQuestions,
    title: data.theme?.title,
  }, options);

  isCreating.value = false;
};

const deleteSession = async (conversationId: string) => {
  const isDeletingOnlySession = sessions.value.length <= 1;

  if (isDeletingOnlySession) {
    await createSession();
  }

  const nextSessions = sessions.value.filter(session => session.conversationId !== conversationId);

  if (nextSessions.length === sessions.value.length || nextSessions.length === 0) {
    return;
  }

  sessions.value = nextSessions;

  saveSessions();

  if (props.activeConversationId === conversationId) {
    selectSession(sessions.value[0].conversationId, {
      showChat: isDeletingOnlySession,
    });
  }
};

watch(() => props.sessionUpdate, applySessionUpdate);

onMounted(async () => {
  sessions.value = safeParseSessions(window.localStorage.getItem(SESSION_STORAGE_KEY));

  if (sessions.value.length === 0) {
    await createSession();

    return;
  }

  emit('sessionsChange', sessions.value);

  const savedActiveSession = window.localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY);
  const activeConversationId = sessions.value.some(session => session.conversationId === savedActiveSession)
    ? savedActiveSession
    : sessions.value[0].conversationId;

  if (activeConversationId) {
    selectSession(activeConversationId);
  }
});

defineExpose({
  createSession,
});
</script>

<template>
  <div
    class="[transition:height_0.25s_ease] absolute inset-0 h-full overflow-y-auto bg-[linear-gradient(180deg,#f8fbff_0%,#fff_100%)] p-16 motion-reduce:[transition:none]"
  >
    <div class="mb-14 flex items-center justify-between gap-12">
      <div class="min-w-0 flex-y-center gap-8">
        <button
          type="button"
          class="size-30 flex-center flex-shrink-0 cursor-pointer border-0 rounded-full bg-#EAF4FF text-#1281FF hover:bg-#DDEEFF"
          aria-label="返回 AI 助手聊天"
          title="返回聊天"
          @click="emit('back')"
        >
          <span class="i-ion:arrow-back block size-18"></span>
        </button>
        <div class="truncate text-15 text-#25324B font-700 leading-22">历史聊天</div>
      </div>
      <button
        type="button"
        class="h-30 cursor-pointer border-0 rounded-15 bg-#1281FF px-12 text-13 text-white font-600 disabled:cursor-not-allowed disabled:bg-#9ECFFF"
        :disabled="isCreating"
        @click="createSession()"
      >
        新建
      </button>
    </div>

    <div class="space-y-8">
      <div
        v-for="session in visibleSessions"
        :key="session.conversationId"
        class="group flex items-center gap-8 border-1 rounded-12 border-solid p-10"
        :class="session.active ? 'border-#1281FF bg-#EAF4FF' : 'border-#E4ECF7 bg-white hover:border-#C7E1FF hover:bg-#F8FBFF'"
      >
        <button
          type="button"
          class="min-w-0 flex-1 cursor-pointer border-0 bg-transparent p-0 text-left"
          @click="selectSession(session.conversationId)"
        >
          <div class="flex items-center justify-between gap-10">
            <span class="truncate text-14 text-#25324B font-700 leading-22">{{ session.title }}</span>
            <span class="flex-shrink-0 text-12 text-#9497A1 leading-18">{{ session.updatedAtText }}</span>
          </div>
          <div class="mt-4 truncate text-12 text-#6B7280 leading-18">
            {{ session.preview }}
          </div>
        </button>

        <button
          type="button"
          class="size-28 flex-center flex-shrink-0 cursor-pointer border-0 rounded-full bg-transparent text-#9AA3B2 opacity-0 hover:bg-#FFECEC hover:text-#D92D20 group-hover:opacity-100"
          aria-label="删除历史聊天"
          title="删除"
          @click.stop="deleteSession(session.conversationId)"
        >
          <span class="i-ion:trash-outline block size-17"></span>
        </button>
      </div>
    </div>
  </div>
</template>
